/**
 * Phase 13 Step 5 + 5.5a — Trajectory Groups card.
 *
 * Step 5.5a wired ``cluster_trajectories(...)`` into the worker, so
 * every completed campaign from that point on ships with a populated
 * ``result.trajectory_groups`` list. This card renders the populated
 * case as its primary state. The "not yet persisted" fallback banner
 * only fires for pre-5.5a campaigns whose persisted sweep document
 * lacks the field (``undefined`` / ``null``), keeping the detail view
 * honest rather than silently showing an empty table.
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Layers } from "lucide-react";

function fmtPk(v) {
    if (v == null || Number.isNaN(Number(v))) return "—";
    return Number(v).toFixed(3);
}

export default function TrajectoryGroupsCard({ campaign }) {
    const groups = campaign?.result?.trajectory_groups;
    // Populated state: array with at least one group.
    const hasGroups = Array.isArray(groups) && groups.length > 0;
    // Legacy (pre-5.5a) state: field missing / explicitly null.
    const isLegacy = !Array.isArray(groups);
    // Empty-array state: post-5.5a campaign where clustering produced
    // zero groups (e.g. zero points). Render as a benign empty-row.
    const isEmptyPost55a = Array.isArray(groups) && groups.length === 0;

    return (
        <Card data-testid="geom-groups-card">
            <CardHeader className="flex flex-row items-center gap-2">
                <Layers className="size-4 text-muted-foreground" />
                <CardTitle className="text-base">Trajectory Groups</CardTitle>
            </CardHeader>
            <CardContent>
                {isLegacy && (
                    <div
                        className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200"
                        data-testid="geom-groups-pending"
                    >
                        Trajectory-group clustering for this campaign is not
                        available — this campaign predates the geometry-worker
                        wiring. Re-run the sweep to
                        populate <code>result.trajectory_groups</code>.
                    </div>
                )}
                {isEmptyPost55a && (
                    <div
                        className="rounded-md border border-ux-border-1 bg-ux-surface-2/40 p-3 text-xs text-muted-foreground"
                        data-testid="geom-groups-empty"
                    >
                        Clustering produced zero groups for this campaign
                        (no simulation outcomes to cluster).
                    </div>
                )}
                {hasGroups && (
                    <Table data-testid="geom-groups-table">
                        <TableHeader>
                            <TableRow>
                                <TableHead>Group</TableHead>
                                <TableHead className="text-center">Members</TableHead>
                                <TableHead>Dominant mode</TableHead>
                                <TableHead className="text-right">Mean Pk</TableHead>
                                <TableHead>Centroid (range × bearing)</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {groups.map((g) => (
                                <TableRow
                                    key={g.group_id}
                                    data-testid={`geom-group-row-${g.group_id}`}
                                >
                                    <TableCell className="tabular-nums">#{g.group_id}</TableCell>
                                    <TableCell
                                        className="text-center tabular-nums"
                                        data-testid={`geom-group-members-${g.group_id}`}
                                    >
                                        {g.n_members}
                                    </TableCell>
                                    <TableCell
                                        className="text-muted-foreground capitalize"
                                        data-testid={`geom-group-mode-${g.group_id}`}
                                    >
                                        {(g.dominant_failure_mode || "—").replaceAll("_", " ")}
                                    </TableCell>
                                    <TableCell
                                        className="text-right tabular-nums"
                                        data-testid={`geom-group-pk-${g.group_id}`}
                                    >
                                        {fmtPk(g.pk_mean_in_group)}
                                    </TableCell>
                                    <TableCell className="text-xs tabular-nums text-muted-foreground">
                                        {g.geometry_centroid
                                            ? `${Math.round(g.geometry_centroid.range_m)}m · `
                                              + `${Math.round(g.geometry_centroid.bearing_deg)}°`
                                            : "—"}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </CardContent>
        </Card>
    );
}
